import { useState, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { Modal, Input, App, Button } from 'antd'
import { DismissibleTooltip } from '../common/DismissibleTooltip'
import { MessageSquare } from 'lucide-react'
import { useAuthContext } from '../../context/auth'
import { useThemeContext } from '../../context/theme'
import { useParams } from 'react-router-dom'
import { HttpClient } from '../../services/api/client'
import { TokenManager } from '../../services/auth/tokenManager'

const { TextArea } = Input

// ============================================================================
// API
// ============================================================================

async function submitFeedback(data: {
  message: string
  user_email?: string
  page_url: string
  screen_size: string
  theme: string
  project_id?: string
}): Promise<void> {
  const headers = await TokenManager.getAuthHeaders()
  const response = await HttpClient.post<{ success: boolean; id: string }>(
    '/feedback',
    data,
    headers
  )

  if (response.error) {
    throw new Error(response.error.message || 'Failed to submit feedback')
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function FeedbackButton() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { user } = useAuthContext()
  const { theme } = useThemeContext()
  const params = useParams()
  const { notification } = App.useApp()

  const openModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setMessage('')
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!message.trim()) return

    setIsSubmitting(true)
    try {
      await submitFeedback({
        message: message.trim(),
        user_email: user?.email ?? undefined,
        page_url: window.location.href,
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        theme,
        project_id: params.id,
      })

      notification.success({
        title: 'Thank you!',
        description: 'Your feedback has been sent.',
        duration: 3,
      })

      closeModal()
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'feedback', action: 'submit_feedback' },
      })
      notification.error({
        title: 'Failed to send',
        description: 'Please try again later.',
        duration: 3,
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [message, user?.email, theme, params.id, notification, closeModal])

  // Only show for authenticated users
  if (!user) return null

  return (
    <>
      <DismissibleTooltip title="Send feedback">
        <Button
          type="text"
          icon={<MessageSquare size={16} />}
          onClick={openModal}
          aria-label="Send feedback"
        />
      </DismissibleTooltip>

      <Modal
        title="Send Feedback"
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        okText="Send"
        okButtonProps={{
          disabled: !message.trim(),
          loading: isSubmitting,
        }}
        destroyOnHidden
      >
        <TextArea
          placeholder="What's on your mind? Share a bug, idea, or anything else..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          autoFocus
          maxLength={2000}
          showCount
          style={{ resize: 'none', marginBottom: 24 }}
        />
      </Modal>
    </>
  )
}
